import {
  EXCLUDE_ALL_OF,
  EXCLUDE_ANY_OF,
  INCLUDE_ALL_OF,
  INCLUDE_ANY_OF,
  MULTI_SELECT_FILTER_MODIFIERS,
  type MultiSelectFilterModifier,
  type MultiSelectFilterValue,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';

import { shouldClampCollectionsModifiers } from '../../constants/search_mappings';

export const STORAGE_CANONICAL_KEY = 'storage_id';

/**
 * A group as it arrives from a backend that names storages per purpose. Loose
 * enough to accept both the NLTF parse schema and an already-built
 * `MultiSelectFilterGroup`.
 */
export type StorageGroupSource = {
  nestedKey: string;
  groups: readonly {
    modifier?: MultiSelectFilterModifier | null;
    items?: string[] | null;
  }[];
};

/**
 * Merge storage groups from the per-purpose nested keys (`files.storage_id`,
 * `proxies.storage_id`, `keyframes.storage_id`) and a flat `storage_id` into a
 * single canonical `storage_id` `MultiSelectFilterValue`.
 *
 * Each item is routed to its originating nested field via `itemTermNames`, so a
 * single `include_any_of` group holding storages from different purposes is
 * compiled to an OR across those nested fields (see
 * `buildMultiSelectFilterCriteria`). Groups are combined by modifier so
 * "files or proxies" collapses into one OR group, while mixed
 * inclusion/exclusion intents remain separate groups that AND together at the
 * top level. `additionalTermNames` keeps the flat `storage_id` term OR:ed in so
 * collections (which store a flat `storage_id`) still match.
 *
 * When `onlyCollectionsSelected` is true and any single group targets more than
 * one storage, `*_all_of` modifiers are clamped to `*_any_of`: a collection
 * lives on a single storage, so an AND across storages could never match (see
 * `shouldClampCollectionsModifiers`).
 */
export function mergeStorageGroups(
  storageGroups: StorageGroupSource[],
  onlyCollectionsSelected: boolean,
): MultiSelectFilterValue {
  // Measure the merged group sizes the same way the search filters panel does:
  // group items by their (original) modifier and take the largest bucket. The
  // panel sees the canonical `storage_id` value post-merge, so measuring the
  // per-modifier merged size here keeps the clamp threshold consistent across
  // both sites (a single incoming group never spans more than one storage on
  // its own, but two purposes can merge into one multi-storage group).
  const distinctByModifier = new Map<MultiSelectFilterModifier, Set<string>>();
  for (const { groups } of storageGroups) {
    for (const group of groups) {
      const modifier = group.modifier ?? INCLUDE_ANY_OF;
      let distinct = distinctByModifier.get(modifier);
      if (!distinct) {
        distinct = new Set<string>();
        distinctByModifier.set(modifier, distinct);
      }
      for (const itemId of group.items ?? []) {
        distinct.add(itemId);
      }
    }
  }
  let maxStoragesInAnyGroup = 0;
  for (const distinct of distinctByModifier.values()) {
    maxStoragesInAnyGroup = Math.max(maxStoragesInAnyGroup, distinct.size);
  }
  const shouldClamp = shouldClampCollectionsModifiers(
    onlyCollectionsSelected,
    maxStoragesInAnyGroup,
  );

  const itemsByModifier = new Map<
    MultiSelectFilterModifier,
    { items: string[]; itemTermNames: Record<string, string> }
  >();

  for (const { nestedKey, groups } of storageGroups) {
    for (const group of groups) {
      const items = group.items ?? [];
      if (items.length === 0) {
        continue;
      }
      let modifier = group.modifier ?? INCLUDE_ANY_OF;
      if (shouldClamp) {
        if (modifier === INCLUDE_ALL_OF) {
          modifier = INCLUDE_ANY_OF;
        } else if (modifier === EXCLUDE_ALL_OF) {
          modifier = EXCLUDE_ANY_OF;
        }
      }
      let entry = itemsByModifier.get(modifier);
      if (!entry) {
        // Null-prototype map: storage ids are the keys, so a value like
        // `__proto__` or `toString` must be a plain data entry, never touch the
        // object's prototype or read back as an inherited member.
        const itemTermNames: Record<string, string> = Object.create(null);
        entry = { items: [], itemTermNames };
        itemsByModifier.set(modifier, entry);
      }
      for (const itemId of items) {
        // A storage id can appear under several nested keys (e.g. both
        // `files.storage_id` and `proxies.storage_id`). Within a modifier we
        // keep a single entry per id, recording one nested key as its
        // `itemTermNames` route. That route only narrows which nested field is
        // matched; the flat `storage_id` in `additionalTermNames` still keeps
        // the id OR:ed across purposes.
        if (!Object.hasOwn(entry.itemTermNames, itemId)) {
          entry.items.push(itemId);
        }
        // A flat `storage_id` predicate must not overwrite an id's nested route
        // (files/proxies/keyframes.storage_id): the nested term is what matches
        // assets, while the flat term is already OR:ed in via
        // `additionalTermNames`. So the flat key only fills a slot that has no
        // nested route yet; a nested key still overwrites (last nested wins).
        const existing = entry.itemTermNames[itemId];
        const incomingIsFlat = nestedKey === STORAGE_CANONICAL_KEY;
        const existingIsNested =
          existing !== undefined && existing !== STORAGE_CANONICAL_KEY;
        if (!(incomingIsFlat && existingIsNested)) {
          entry.itemTermNames[itemId] = nestedKey;
        }
      }
    }
  }

  const merged: MultiSelectFilterValue = [];
  for (const modifier of MULTI_SELECT_FILTER_MODIFIERS) {
    const entry = itemsByModifier.get(modifier);
    if (!entry || entry.items.length === 0) {
      continue;
    }
    merged.push({
      modifier,
      items: entry.items,
      itemTermNames: entry.itemTermNames,
      additionalTermNames: [STORAGE_CANONICAL_KEY],
    });
  }
  return merged;
}
